import React, { useEffect, useState, useRef } from 'react';
import ExcelJS from 'exceljs';
import ipsService from '../services/ipsService';
import IpsScanSummary from '../components/IpsScanSummary';
import IpsScanClientTable from '../components/IpsScanClientTable';

// How often to poll the active scan run for status updates.
const RUN_POLL_INTERVAL_MS = 3000;

const EXPORT_COLUMN_DEFS = [
  { header: 'Client', key: 'client', width: 36 },
  { header: 'Status', key: 'status', width: 14 },
  { header: 'Missing Required Fields', key: 'missingRequiredFields', width: 24 },
  { header: 'Date of Agreement', key: 'dateOfAgreement', width: 22 },
  { header: 'Domestic Stocks or Domestic Stock Funds', key: 'domesticStocks', width: 32 },
  { header: 'International Stocks or International Stock Funds', key: 'internationalStocks', width: 36 },
  {
    header: 'Bonds (Bonds, Bond Funds, Muni Bonds, Govt Treasuries, CDs, etc.)',
    key: 'bonds',
    width: 42,
  },
  {
    header: 'Alternative Investments (Real estate, Commodity Funds, Allocation Funds, etc.)',
    key: 'alternativeInvestments',
    width: 48,
  },
  { header: 'Money Market', key: 'moneyMarket', width: 20 },
  { header: 'Concentration Positions', key: 'concentrationPositions', width: 30 },
  { header: 'Other Unique Language', key: 'otherLanguage', width: 40 },
];

const STATUS_EXPORT_LABELS = {
  queued: 'Queued',
  scanning: 'Scanning',
  found: 'IPS Found',
  missing: 'No Match',
  error: 'Error',
};

const STATUS_SORT_ORDER = {
  queued: 1,
  scanning: 2,
  found: 3,
  missing: 4,
  error: 5,
};

const STATUS_FILTER_OPTIONS = [
  { key: 'queued', label: 'Queued' },
  { key: 'scanning', label: 'Scanning' },
  { key: 'found', label: 'IPS Found' },
  { key: 'missing', label: 'No Match' },
  { key: 'error', label: 'Error' },
  { key: 'reason:no_alignment', label: 'No Alignment' },
  { key: 'reason:inactive_client', label: 'Inactive Client' },
];

const REQUIRED_FIELD_DEFS = [
  { key: 'dateOfAgreement', label: 'Date of Agreement' },
  { key: 'domesticStocks', label: 'Domestic Stocks or Domestic Stock Funds' },
  { key: 'internationalStocks', label: 'International Stocks or International Stock Funds' },
  { key: 'bonds', label: 'Bonds (Bonds, Bond Funds, Muni Bonds, Govt Treasuries, CDs, etc.)' },
  {
    key: 'alternativeInvestments',
    label: 'Alternative Investments (Real estate, Commodity Funds, Allocation Funds, etc.)',
  },
  { key: 'moneyMarket', label: 'Money Market' },
];

/**
 * IpsScanner
 *
 * IPS (Investment Policy Statement) document scanner.
 *
 * A "Run Scan" click starts a scan run on the backend (an AWS Step Functions
 * state machine orchestrates bounded-concurrency per-client SharePoint
 * scanning + per-file Bedrock field-extraction analysis, with retries for
 * SharePoint throttling). All progress is persisted server-side (DynamoDB),
 * keyed by a runId, so:
 *   - this page just polls GET /ips/scan-runs/{runId} for live status
 *     instead of driving the scan itself, and
 *   - on mount, it fetches the most recent run (GET /ips/scan-runs/latest)
 *     and resumes polling automatically if that run is still in progress -
 *     so navigating away and back (or a page refresh) never loses progress
 *     or forces starting over.
 */
const IpsScanner = () => {
  const [rows, setRows] = useState([]); // ordered array of { clientName, status, files, error, reasonNote }
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [scannedAt, setScannedAt] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [sortBy, setSortBy] = useState('client');
  const [sortDirection, setSortDirection] = useState('asc');
  const [debugLimit, setDebugLimit] = useState(null); // { totalAvailable } when the backend limited the client list
  const [fileAnalyses, setFileAnalyses] = useState({}); // fileId -> IpsFileAnalysisResult
  const [exportingExcel, setExportingExcel] = useState(false);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [skippedClients, setSkippedClients] = useState([]);
  const [loadingSkippedClients, setLoadingSkippedClients] = useState(false);
  const [manuallyScanningSkipped, setManuallyScanningSkipped] = useState({});
  const [showSkippedClients, setShowSkippedClients] = useState(false);
  const [renameDrafts, setRenameDrafts] = useState({});
  const [selectedRenameFileIds, setSelectedRenameFileIds] = useState({});
  const [renamingFileIds, setRenamingFileIds] = useState({});
  const [renamingSelected, setRenamingSelected] = useState(false);
  const [renameTargetOverridesByClient, setRenameTargetOverridesByClient] = useState({});

  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const isRunActuallyComplete = (detail) => {
    if (!detail) return false;
    // Wait for backend finalization so any post-analysis client status/reason
    // adjustments (for example found->missing with reasonNote) are included.
    return detail.status === 'completed';
  };

  const loadSkippedClients = async () => {
    setLoadingSkippedClients(true);
    try {
      const clientInfo = await ipsService.getClients();
      setSkippedClients(clientInfo.skippedClients || []);
    } catch (err) {
      setSkippedClients([]);
    } finally {
      setLoadingSkippedClients(false);
    }
  };

  // Maps a ScanRunDetail (run + every client + every matched file's status)
  // onto this component's existing rows/fileAnalyses state shapes, so the
  // presentational child components (IpsScanSummary/IpsScanClientTable/
  // IpsScanFileRow/IpsFileAnalysisPanel) need no changes at all.
  const applyRunDetail = (detail) => {
    if (!detail) return;

    setCurrentRunId(detail.runId);
    setDebugLimit(detail.debugLimited ? { totalAvailable: detail.totalAvailable } : null);

    const newRows = (detail.clients || []).map((client) => ({
      clientName: client.clientName,
      status: client.status,
      error: client.error || null,
      reasonNote: client.reasonNote || null,
      files: (client.files || []).map((file) => ({
        fileId: file.fileId,
        name: file.name,
        path: file.path,
        folderPath: file.folderPath,
        size: file.size,
        modifiedAt: file.modifiedAt,
        webUrl: file.webUrl,
        analysisStatus: file.analysisStatus,
        renameStatus: file.renameStatus,
        renamedName: file.renamedName,
        renameError: file.renameError,
        renameUpdatedAt: file.renameUpdatedAt,
      })),
    }));
    setRows(newRows);

    const newFileAnalyses = {};
    (detail.clients || []).forEach((client) => {
      (client.files || []).forEach((file) => {
        newFileAnalyses[file.fileId] = {
          fileId: file.fileId,
          clientName: client.clientName,
          fileName: file.name,
          status: file.analysisStatus,
          fields: file.fields || null,
          rawResponse: file.rawResponse || null,
          error: file.analysisError || null,
          updatedAt: file.analysisUpdatedAt || null,
        };
      });
    });
    setFileAnalyses(newFileAnalyses);

    if (detail.status === 'completed') {
      setScannedAt(detail.updatedAt || new Date().toISOString());
    }
  };

  const pollRun = async (runId) => {
    try {
      const detail = await ipsService.getScanRun(runId);
      applyRunDetail(detail);
      const clients = detail.clients || [];
      const clientScanPhaseDone = clients.length > 0 && clients.every(
        (client) => client.status !== 'queued' && client.status !== 'scanning'
      );
      if (clientScanPhaseDone) {
        setScanning(false);
      }
      if (isRunActuallyComplete(detail)) {
        stopPolling();
        setScanning(false);
        setScannedAt(detail.updatedAt || new Date().toISOString());
      }
    } catch (err) {
      if (err.response?.status === 404) {
        stopPolling();
        setScanning(false);
        setError('This scan run could not be found. Please start a new scan.');
      }
      // Otherwise treat as transient (network hiccup) and let the next tick retry.
    }
  };

  const startPolling = (runId) => {
    stopPolling();
    pollRun(runId);
    pollTimerRef.current = setInterval(() => pollRun(runId), RUN_POLL_INTERVAL_MS);
  };

  // On mount: resume/show the most recently started run (if any), including
  // reattaching live polling if that run was still in progress.
  useEffect(() => {
    (async () => {
      await loadSkippedClients();
      try {
        const detail = await ipsService.getLatestScanRun();
        applyRunDetail(detail);
        if (!isRunActuallyComplete(detail)) {
          setScanning(true);
          startPolling(detail.runId);
        } else {
          setScanning(false);
        }
      } catch (err) {
        // No prior scan runs yet (404) - leave the initial empty state as-is.
      }
    })();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runScan = async () => {
    setScanning(true);
    setError('');
    setScannedAt(null);
    setActiveStatuses([]);
    setRows([]);
    setDebugLimit(null);
    setFileAnalyses({});
    setRenameDrafts({});
    setSelectedRenameFileIds({});
    setRenamingFileIds({});
    setRenameTargetOverridesByClient({});

    try {
      const started = await ipsService.startScanRun();
      startPolling(started.runId);
      await loadSkippedClients();
    } catch (err) {
      console.error('IPS scan failed to start:', err);
      setScanning(false);
      setError(
        err.response?.data?.detail || err.message || 'Failed to start the scan. Please try again.'
      );
    }
  };

  // Re-runs scanning (and analysis) for just the clients that previously
  // errored out, reusing the same run so already-completed rows are left
  // untouched and aggregate counts refresh once the retry finishes.
  const retryFailedClients = async () => {
    if (!currentRunId) return;
    const failedNames = rows.filter((row) => row.status === 'error').map((row) => row.clientName);
    if (failedNames.length === 0) return;

    setScanning(true);
    setError('');

    try {
      await ipsService.retryScanRunClients(currentRunId, failedNames);
      startPolling(currentRunId);
    } catch (err) {
      setScanning(false);
      setError(err.response?.data?.detail || err.message || 'Failed to retry failed clients.');
    }
  };

  const upsertClientRow = (nextRow) => {
    setRows((prev) => {
      const filtered = prev.filter((row) => row.clientName !== nextRow.clientName);
      const merged = [...filtered, nextRow];
      merged.sort((a, b) => a.clientName.localeCompare(b.clientName));
      return merged;
    });
  };

  const scanSkippedClient = async (clientName) => {
    setError('');
    setManuallyScanningSkipped((prev) => ({ ...prev, [clientName]: true }));
    try {
      const result = await ipsService.scanClient(clientName);
      const normalizedRow = {
        clientName: result.clientName,
        status: result.error ? 'error' : (result.hasIps ? 'found' : 'missing'),
        error: result.error || null,
        reasonNote: result.reasonNote || null,
        files: (result.files || []).map((file) => ({
          fileId: file.fileId,
          name: file.name,
          path: file.path,
          folderPath: file.folderPath,
          size: file.size,
          modifiedAt: file.modifiedAt,
          webUrl: file.webUrl,
          analysisStatus: file.analysisStatus,
        })),
      };
      upsertClientRow(normalizedRow);
      setScannedAt(new Date().toISOString());
    } catch (err) {
      setError(
        err.response?.data?.detail
          || err.message
          || `Failed to scan skipped folder ${clientName}.`
      );
    } finally {
      setManuallyScanningSkipped((prev) => {
        const next = { ...prev };
        delete next[clientName];
        return next;
      });
    }
  };

  const refreshCurrentRun = async () => {
    if (!currentRunId) return;
    const detail = await ipsService.getScanRun(currentRunId);
    applyRunDetail(detail);
  };

  const handleRenameDraftChange = (clientName, file, value) => {
    if (effectiveRenameFileIdsByClient[clientName] !== file.fileId) return;
    setRenameDrafts((prev) => ({
      ...prev,
      [file.fileId]: {
        suggestedName: prev[file.fileId]?.suggestedName || '',
        value,
      },
    }));
  };

  const handleToggleRenameSelect = (clientName, file, checked) => {
    if (effectiveRenameFileIdsByClient[clientName] !== file.fileId) return;
    setSelectedRenameFileIds((prev) => {
      const next = { ...prev };
      if (checked) {
        next[file.fileId] = true;
      } else {
        delete next[file.fileId];
      }
      return next;
    });
  };

  const handleSetRenameTarget = (clientName, file) => {
    setRenameTargetOverridesByClient((prev) => ({
      ...prev,
      [clientName]: file.fileId,
    }));

    setRenameDrafts((prev) => {
      if (prev[file.fileId]?.value) {
        return prev;
      }
      const analysis = fileAnalyses[file.fileId];
      const suggestedName = buildSuggestedRename(clientName, file, analysis);
      return {
        ...prev,
        [file.fileId]: {
          suggestedName,
          value: suggestedName,
        },
      };
    });
  };

  const renameSingleFile = async (clientName, file) => {
    if (!currentRunId) return;
    if (effectiveRenameFileIdsByClient[clientName] !== file.fileId) return;

    const draftValue = (renameDrafts[file.fileId]?.value || '').trim();
    if (!draftValue) return;

    setError('');
    setRenamingFileIds((prev) => ({ ...prev, [file.fileId]: true }));
    try {
      await ipsService.renameRunFile(currentRunId, {
        clientName,
        fileId: file.fileId,
        newName: draftValue,
        allowNonLatest: true,
      });
      await refreshCurrentRun();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to rename file.');
    } finally {
      setRenamingFileIds((prev) => {
        const next = { ...prev };
        delete next[file.fileId];
        return next;
      });
    }
  };

  const renameSelectedLatestFiles = async () => {
    if (!currentRunId || selectedLatestRenameTargets.length === 0) return;
    setError('');
    setRenamingSelected(true);

    const selectedIds = selectedLatestRenameTargets.map((item) => item.fileId);
    setRenamingFileIds((prev) => {
      const next = { ...prev };
      selectedIds.forEach((fileId) => {
        next[fileId] = true;
      });
      return next;
    });

    try {
      const result = await ipsService.renameRunFiles(currentRunId, {
        files: selectedLatestRenameTargets,
      });
      await refreshCurrentRun();

      if (result.failed > 0) {
        setError(`Bulk rename completed with ${result.failed} failure(s).`);
      } else {
        setSelectedRenameFileIds({});
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to rename selected files.');
    } finally {
      setRenamingSelected(false);
      setRenamingFileIds((prev) => {
        const next = { ...prev };
        selectedIds.forEach((fileId) => {
          delete next[fileId];
        });
        return next;
      });
    }
  };

  const parseAgreementDateToTimestamp = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Date.parse(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const parseIsoDateToTimestamp = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const sanitizeToken = (value) => (
    (value || '')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );

  const splitClientLastFirst = (clientName) => {
    const normalized = (clientName || '').trim();
    if (!normalized) return { last: 'Client', first: '' };

    const baseName = normalized.split('_')[0].trim();
    if (baseName.includes(',')) {
      const [lastRaw, firstRaw] = baseName.split(',', 2);
      const firstPrimary = (firstRaw || '').split(' and ')[0];
      return {
        last: sanitizeToken(lastRaw) || 'Client',
        first: sanitizeToken(firstPrimary),
      };
    }

    const parts = baseName.split(/\s+/).map(sanitizeToken).filter(Boolean);
    if (parts.length === 0) return { last: 'Client', first: '' };
    if (parts.length === 1) return { last: parts[0], first: '' };
    return { last: parts[parts.length - 1], first: parts[0] };
  };

  const parseFlexibleDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const direct = Date.parse(trimmed);
    if (!Number.isNaN(direct)) return new Date(direct);

    const mmdd = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2}|\d{4})$/);
    if (mmdd) {
      let year = Number(mmdd[3]);
      if (year < 100) year += 2000;
      const month = Number(mmdd[1]) - 1;
      const day = Number(mmdd[2]);
      const d = new Date(Date.UTC(year, month, day));
      if (!Number.isNaN(d.getTime())) return d;
    }

    return null;
  };

  const looksLikeAllBundle = (fileName) => (
    /(^|[^a-z0-9])all([^a-z0-9]|$)/i.test(fileName || '')
  );

  const toIsoDate = (dateObj) => (
    dateObj
      ? new Date(dateObj.getTime() - (dateObj.getTimezoneOffset() * 60000))
        .toISOString()
        .slice(0, 10)
      : new Date().toISOString().slice(0, 10)
  );

  const buildSuggestedRename = (clientName, file, analysis) => {
    const { last, first } = splitClientLastFirst(clientName);
    const agreementDate = analysis?.status === 'complete' ? analysis?.fields?.dateOfAgreement : null;
    const preferredDate = parseFlexibleDate(agreementDate) || parseFlexibleDate(file.modifiedAt) || new Date();
    const contractTag = looksLikeAllBundle(file.name) ? 'ALL' : 'IPS';
    const nameParts = [last];
    if (first) nameParts.push(first);
    nameParts.push(contractTag, 'Signed', toIsoDate(preferredDate));
    return `${nameParts.join('-')}.pdf`;
  };

  /**
   * Returns true when a file has been confirmed by the LLM as NOT being an
   * Investment Policy Statement and should therefore be hidden from the UI.
   */
  const isConfirmedNonIps = (fileId) => {
    const analysis = fileAnalyses[fileId];
    return analysis?.status === 'complete' && analysis?.fields?.isIps === false;
  };

  const getEffectiveStatusForRow = (row) => {
    if (row.status !== 'found') return row.status;
    const hasVisibleIpsFile = (row.files || []).some((file) => !isConfirmedNonIps(file.fileId));
    return hasVisibleIpsFile ? 'found' : 'missing';
  };

  const getMissingReasonFilterKey = (row) => {
    if (getEffectiveStatusForRow(row) !== 'missing') return null;
    const reason = (row.reasonNote || '').trim().toLowerCase();
    if (reason === 'no alignment') return 'reason:no_alignment';
    if (reason === 'inactive client') return 'reason:inactive_client';
    return null;
  };

  const isGenericNoMatchRow = (row) => {
    if (getEffectiveStatusForRow(row) !== 'missing') return false;
    const missingReasonKey = getMissingReasonFilterKey(row);
    if (missingReasonKey) return false;
    const reason = (row.reasonNote || '').trim().toLowerCase();
    return reason.length === 0 || reason === 'no match';
  };

  const getRowFilterKeys = (row) => {
    const keys = [];
    const effectiveStatus = getEffectiveStatusForRow(row);
    if (effectiveStatus !== 'missing') {
      keys.push(effectiveStatus);
    }

    if (isGenericNoMatchRow(row)) {
      keys.push('missing');
    }

    const missingReasonKey = getMissingReasonFilterKey(row);
    if (missingReasonKey) {
      keys.push(missingReasonKey);
    }
    return keys;
  };

  const counts = rows.reduce(
    (acc, row) => {
      const effectiveStatus = getEffectiveStatusForRow(row);
      const rowFilterKeys = getRowFilterKeys(row);

      acc.total += 1;
      if (effectiveStatus === 'missing') {
        acc.missingTotal += 1;
      }

      if (rowFilterKeys.includes('queued')) acc.queued += 1;
      if (rowFilterKeys.includes('scanning')) acc.scanning += 1;
      if (rowFilterKeys.includes('found')) acc.found += 1;
      if (rowFilterKeys.includes('error')) acc.error += 1;
      if (rowFilterKeys.includes('missing')) acc.missing += 1;
      if (rowFilterKeys.includes('reason:no_alignment')) acc.noAlignment += 1;
      if (rowFilterKeys.includes('reason:inactive_client')) acc.inactiveClient += 1;

      return acc;
    },
    {
      total: 0,
      queued: 0,
      scanning: 0,
      found: 0,
      missing: 0,
      missingTotal: 0,
      noAlignment: 0,
      inactiveClient: 0,
      error: 0,
    }
  );

  const pickBestFileForClient = (files = []) => {
    if (!files.length) return null;

    // Exclude any files that the LLM has confirmed are NOT IPS documents.
    // This keeps confirmed non-IPS files from being treated as the "best"
    // (most recent / most complete) IPS for the client.
    const candidates = files.filter((f) => !isConfirmedNonIps(f.fileId));
    if (!candidates.length) return null;

    return candidates.reduce((best, file) => {
      if (!best) return file;

      const bestAnalysis = fileAnalyses[best.fileId];
      const candidateAnalysis = fileAnalyses[file.fileId];

      const bestAgreementTs = parseAgreementDateToTimestamp(
        bestAnalysis?.status === 'complete' ? bestAnalysis.fields?.dateOfAgreement : null
      );
      const candidateAgreementTs = parseAgreementDateToTimestamp(
        candidateAnalysis?.status === 'complete' ? candidateAnalysis.fields?.dateOfAgreement : null
      );

      const bestHasAgreementDate = bestAgreementTs !== null;
      const candidateHasAgreementDate = candidateAgreementTs !== null;

      if (bestHasAgreementDate && !candidateHasAgreementDate) return best;
      if (!bestHasAgreementDate && candidateHasAgreementDate) return file;
      if (bestHasAgreementDate && candidateHasAgreementDate) {
        if (candidateAgreementTs > bestAgreementTs) return file;
        if (candidateAgreementTs < bestAgreementTs) return best;
      }

      const bestModifiedTs = parseIsoDateToTimestamp(best.modifiedAt);
      const candidateModifiedTs = parseIsoDateToTimestamp(file.modifiedAt);
      if (bestModifiedTs !== null && candidateModifiedTs !== null) {
        if (candidateModifiedTs > bestModifiedTs) return file;
        if (candidateModifiedTs < bestModifiedTs) return best;
      }
      if (bestModifiedTs === null && candidateModifiedTs !== null) return file;
      if (bestModifiedTs !== null && candidateModifiedTs === null) return best;

      return (file.name || '').localeCompare(best.name || '') > 0 ? file : best;
    }, null);
  };

  const latestRenameFileIdsByClient = rows.reduce((acc, row) => {
    if (row.status !== 'found') return acc;
    const bestFile = pickBestFileForClient(row.files || []);
    if (bestFile?.fileId) {
      acc[row.clientName] = bestFile.fileId;
    }
    return acc;
  }, {});

  const effectiveRenameFileIdsByClient = rows.reduce((acc, row) => {
    const fallbackFileId = latestRenameFileIdsByClient[row.clientName];
    const overrideFileId = renameTargetOverridesByClient[row.clientName];
    const visibleFileIds = new Set(
      (row.files || [])
        .filter((file) => !isConfirmedNonIps(file.fileId))
        .map((file) => file.fileId)
    );

    if (overrideFileId && visibleFileIds.has(overrideFileId)) {
      acc[row.clientName] = overrideFileId;
      return acc;
    }

    if (fallbackFileId) {
      acc[row.clientName] = fallbackFileId;
    }
    return acc;
  }, {});

  useEffect(() => {
    setRenameDrafts((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        const latestFileId = effectiveRenameFileIdsByClient[row.clientName];
        if (!latestFileId) return;
        const targetFile = (row.files || []).find((file) => file.fileId === latestFileId);
        if (!targetFile) return;
        const analysis = fileAnalyses[latestFileId];
        const suggestedName = buildSuggestedRename(row.clientName, targetFile, analysis);
        const currentDraft = next[latestFileId]?.value;
        next[latestFileId] = {
          suggestedName,
          value: currentDraft || suggestedName,
        };
      });
      return next;
    });

    setSelectedRenameFileIds((prev) => {
      const valid = {};
      Object.values(effectiveRenameFileIdsByClient).forEach((fileId) => {
        if (prev[fileId]) {
          valid[fileId] = true;
        }
      });
      return valid;
    });
  }, [rows, fileAnalyses, effectiveRenameFileIdsByClient]);

  useEffect(() => {
    setRenameTargetOverridesByClient((prev) => {
      const next = {};
      rows.forEach((row) => {
        const overrideFileId = prev[row.clientName];
        if (!overrideFileId) return;
        const stillVisible = (row.files || []).some(
          (file) => file.fileId === overrideFileId && !isConfirmedNonIps(file.fileId)
        );
        if (stillVisible) {
          next[row.clientName] = overrideFileId;
        }
      });
      return next;
    });
  }, [rows, fileAnalyses]);

  const selectedLatestRenameTargets = rows
    .map((row) => {
      const latestFileId = effectiveRenameFileIdsByClient[row.clientName];
      if (!latestFileId || !selectedRenameFileIds[latestFileId]) return null;
      const file = (row.files || []).find((candidate) => candidate.fileId === latestFileId);
      if (!file) return null;
      const newName = (renameDrafts[latestFileId]?.value || '').trim();
      if (!newName) return null;
      return {
        clientName: row.clientName,
        fileId: latestFileId,
        newName,
        allowNonLatest: true,
      };
    })
    .filter(Boolean);

  const selectableRenameTargetFileIds = rows
    .map((row) => effectiveRenameFileIdsByClient[row.clientName])
    .filter(Boolean);

  const selectedRenameTargetCount = selectableRenameTargetFileIds
    .filter((fileId) => !!selectedRenameFileIds[fileId])
    .length;

  const allRenameTargetsSelected =
    selectableRenameTargetFileIds.length > 0
    && selectedRenameTargetCount === selectableRenameTargetFileIds.length;

  const handleToggleSelectAllRenameTargets = (checked) => {
    if (!checked) {
      setSelectedRenameFileIds({});
      return;
    }

    const nextSelected = {};
    selectableRenameTargetFileIds.forEach((fileId) => {
      nextSelected[fileId] = true;
    });
    setSelectedRenameFileIds(nextSelected);
  };

  const getMissingRequiredFieldLabels = (analysis) => {
    if (!analysis || analysis.status !== 'complete') {
      return [];
    }

    // If the LLM explicitly determined this is not an IPS document, skip the
    // missing-required-fields check entirely - there is nothing to warn about.
    if (analysis.fields?.isIps === false) {
      return [];
    }

    const fields = analysis.fields || {};
    return REQUIRED_FIELD_DEFS
      .filter(({ key }) => {
        const value = fields[key];
        if (value === null || value === undefined) return true;
        if (typeof value !== 'string') return false;
        return value.trim() === '';
      })
      .map(({ label }) => label);
  };

  const getTargetFileForRow = (row) => {
    if (row.status !== 'found') return null;
    const targetFileId = effectiveRenameFileIdsByClient[row.clientName];
    if (!targetFileId) return null;
    return (row.files || []).find((file) => file.fileId === targetFileId) || null;
  };

  const getTargetAnalysisForRow = (row) => {
    const targetFile = getTargetFileForRow(row);
    if (!targetFile) return null;
    return fileAnalyses[targetFile.fileId] || null;
  };

  const missingRequiredByClient = rows.reduce((acc, row) => {
    if (getEffectiveStatusForRow(row) !== 'found') return acc;
    const targetAnalysis = getTargetAnalysisForRow(row);
    const missingFieldLabels = getMissingRequiredFieldLabels(targetAnalysis);
    acc[row.clientName] = {
      hasMissingRequired: missingFieldLabels.length > 0,
      missingFieldLabels,
    };
    return acc;
  }, {});

  const hasActiveStatusFilter = activeStatuses.length > 0;
  const filteredRows = hasActiveStatusFilter
    ? rows.filter((row) => {
      const rowFilterKeys = getRowFilterKeys(row);
      return activeStatuses.some((activeStatus) => rowFilterKeys.includes(activeStatus));
    })
    : rows;

  const filteredAndSortedRows = [...filteredRows].sort((a, b) => {
    const clientSort = a.clientName.localeCompare(b.clientName);
    if (sortBy === 'status') {
      const statusDiff =
        (STATUS_SORT_ORDER[getEffectiveStatusForRow(a)] || 999)
        - (STATUS_SORT_ORDER[getEffectiveStatusForRow(b)] || 999);
      if (statusDiff !== 0) {
        return sortDirection === 'asc' ? statusDiff : -statusDiff;
      }
      return sortDirection === 'asc' ? clientSort : -clientSort;
    }

    return sortDirection === 'asc' ? clientSort : -clientSort;
  });

  const analysesFinished = rows.every((row) => {
    if (row.status !== 'found') return true;
    const files = row.files || [];
    return files.every((file) => {
      const analysisStatus = fileAnalyses[file.fileId]?.status;
      return analysisStatus === 'complete' || analysisStatus === 'error';
    });
  });

  const canExportIpsExcel =
    filteredAndSortedRows.length > 0 &&
    !scanning &&
    !exportingExcel &&
    counts.queued === 0 &&
    counts.scanning === 0 &&
    analysesFinished;

  const exportMissingToCsv = () => {
    const missing = rows.filter((r) => getEffectiveStatusForRow(r) === 'missing');
    if (missing.length === 0) return;
    const csvRows = ['Client Name,Reason'];
    missing.forEach((r) => {
      const safeName = r.clientName.replace(/"/g, '""');
      const safeReason = (r.reasonNote || '').replace(/"/g, '""');
      csvRows.push(`"${safeName}","${safeReason}"`);
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_missing_ips_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportIpsToExcel = async () => {
    if (!canExportIpsExcel) return;
    setExportingExcel(true);

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('IPS Scan Export');
      worksheet.columns = EXPORT_COLUMN_DEFS;

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { vertical: 'top', wrapText: true };

      filteredAndSortedRows.forEach((row) => {
        const effectiveStatus = getEffectiveStatusForRow(row);
        const missingRequiredInfo = missingRequiredByClient[row.clientName] || {
          hasMissingRequired: false,
          missingFieldLabels: [],
        };
        const exportRow = {
          client: row.clientName,
          status: (
            effectiveStatus === 'missing' && (row.reasonNote || '').trim()
              ? (row.reasonNote || '').trim()
              : (STATUS_EXPORT_LABELS[effectiveStatus] || effectiveStatus)
          ),
          missingRequiredFields: effectiveStatus === 'found' && missingRequiredInfo.hasMissingRequired ? 'Yes' : 'No',
          dateOfAgreement: '',
          domesticStocks: '',
          internationalStocks: '',
          bonds: '',
          alternativeInvestments: '',
          moneyMarket: '',
          concentrationPositions: '',
          otherLanguage: '',
        };

        if (effectiveStatus === 'found') {
          const targetAnalysis = getTargetAnalysisForRow(row);
          const fields = targetAnalysis?.status === 'complete' ? (targetAnalysis.fields || {}) : {};
          const concentrationPositions = Array.isArray(fields.concentrationPositions)
            ? fields.concentrationPositions.filter(Boolean).join(', ')
            : (fields.concentrationPositions || '');

          exportRow.dateOfAgreement = fields.dateOfAgreement || '';
          exportRow.domesticStocks = fields.domesticStocks || '';
          exportRow.internationalStocks = fields.internationalStocks || '';
          exportRow.bonds = fields.bonds || '';
          exportRow.alternativeInvestments = fields.alternativeInvestments || '';
          exportRow.moneyMarket = fields.moneyMarket || '';
          exportRow.concentrationPositions = concentrationPositions;
          exportRow.otherLanguage = fields.otherLanguage || '';
        }

        worksheet.addRow(exportRow);
      });

      worksheet.eachRow((worksheetRow, rowNumber) => {
        if (rowNumber === 1) return;
        worksheetRow.alignment = { vertical: 'top', wrapText: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([
        buffer,
      ], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ips_scan_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortDirection('asc');
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>IPS Document Scanner</h2>
          <p className="text-muted mb-0">
            Scans each client's SharePoint <code>Client Services Docs/Contracts</code> folder
            (and sub-folders) for Investment Policy Statement documents (filenames containing
            &ldquo;IPS&rdquo; anywhere). Each matched file is then analysed by an LLM to
            confirm it is a real IPS and extract key fields. Files the LLM determines are
            not genuine IPS documents are hidden from the results.
          </p>
        </div>
        <div>
          <button
            className="btn btn-primary"
            onClick={runScan}
            disabled={scanning}
          >
            {scanning ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Scanning...
              </>
            ) : (
              <>
                <i className="bi bi-search me-2"></i>
                Run Scan
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {debugLimit && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-bug me-1"></i>
          Local debug mode: limited to the first {rows.length} of {debugLimit.totalAvailable} clients
          (set <code>IPS_DEBUG_LIMIT_CLIENTS=false</code> to scan everyone).
        </div>
      )}

      {skippedClients.length > 0 && (
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none fw-semibold"
                onClick={() => setShowSkippedClients((prev) => !prev)}
              >
                <i className={`bi ${showSkippedClients ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
                <strong>Skipped Client Folders</strong>
                <span className="text-muted ms-2">({skippedClients.length})</span>
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={loadSkippedClients}
                disabled={loadingSkippedClients}
              >
                {loadingSkippedClients ? 'Refreshing...' : 'Refresh List'}
              </button>
            </div>
            {showSkippedClients && (
              <div className="mt-2">
                {skippedClients.map((clientName) => (
                  <div
                    key={clientName}
                    className="d-flex justify-content-between align-items-center border-bottom py-1"
                  >
                    <span>{clientName}</span>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => scanSkippedClient(clientName)}
                      disabled={!!manuallyScanningSkipped[clientName] || scanning}
                    >
                      {manuallyScanningSkipped[clientName] ? 'Scanning...' : 'Scan This Folder'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {rows.length === 0 && !scanning && !error && (
        <div className="alert alert-info">
          Click <strong>Run Scan</strong> to check every SharePoint client for an IPS document.
        </div>
      )}

      {rows.length > 0 && (
        <>
          <IpsScanSummary
            counts={counts}
            activeStatuses={activeStatuses}
            onStatusToggle={(statusKey) => {
              setActiveStatuses((prev) => (
                prev.includes(statusKey)
                  ? prev.filter((status) => status !== statusKey)
                  : [...prev, statusKey]
              ));
            }}
            onClearFilters={() => setActiveStatuses([])}
            scannedAt={scannedAt}
          />

          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Filter status:</span>
              {STATUS_FILTER_OPTIONS.map((option) => {
                const isActive = activeStatuses.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => {
                      setActiveStatuses((prev) => (
                        prev.includes(option.key)
                          ? prev.filter((status) => status !== option.key)
                          : [...prev, option.key]
                      ));
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
              {activeStatuses.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => setActiveStatuses([])}
                >
                  Clear
                </button>
              )}
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleToggleSelectAllRenameTargets(true)}
                disabled={
                  scanning
                  || renamingSelected
                  || !currentRunId
                  || selectableRenameTargetFileIds.length === 0
                  || allRenameTargetsSelected
                }
                title="Select all current target files"
              >
                Check All Targets ({selectedRenameTargetCount}/{selectableRenameTargetFileIds.length})
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleToggleSelectAllRenameTargets(false)}
                disabled={
                  scanning
                  || renamingSelected
                  || selectedRenameTargetCount === 0
                }
                title="Clear all selected target files"
              >
                Clear Checks
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={renameSelectedLatestFiles}
                disabled={
                  scanning
                  || renamingSelected
                  || !currentRunId
                  || selectedLatestRenameTargets.length === 0
                }
                title="Rename selected target files"
              >
                {renamingSelected ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Renaming...
                  </>
                ) : (
                  <>
                    <i className="bi bi-pencil-square me-1"></i>
                    Rename Selected Targets ({selectedLatestRenameTargets.length})
                  </>
                )}
              </button>
              {counts.error > 0 && (
                <button
                  className="btn btn-outline-warning btn-sm"
                  onClick={retryFailedClients}
                  disabled={scanning}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Retry Failed ({counts.error})
                </button>
              )}
              <button
                className="btn btn-outline-success btn-sm"
                onClick={exportIpsToExcel}
                disabled={!canExportIpsExcel}
                title={canExportIpsExcel ? 'Export IPS scan results to Excel using selected target files' : 'Available after all scans and analyses finish'}
              >
                {exportingExcel ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Exporting Excel...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel me-1"></i>
                    Export IPS Targets (Excel)
                  </>
                )}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={exportMissingToCsv}
                disabled={counts.missingTotal === 0}
              >
                <i className="bi bi-download me-1"></i>
                Export No-Match List (CSV)
              </button>
            </div>
          </div>

          <IpsScanClientTable
            rows={filteredAndSortedRows}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            fileAnalyses={fileAnalyses}
            missingRequiredByClient={missingRequiredByClient}
            isConfirmedNonIps={isConfirmedNonIps}
            renameTargetFileIdsByClient={effectiveRenameFileIdsByClient}
            renameDrafts={renameDrafts}
            selectedRenameFileIds={selectedRenameFileIds}
            renamingFileIds={renamingFileIds}
            onRenameDraftChange={handleRenameDraftChange}
            onToggleRenameSelect={handleToggleRenameSelect}
            onRenameFile={renameSingleFile}
            onSetRenameTarget={handleSetRenameTarget}
          />
        </>
      )}
    </div>
  );
};

export default IpsScanner;

